<template>
    <div>
        <pa-section title="Versões do termo" :description="intro" flush>
            <template #actions>
                <v-btn color="primary" depressed :loading="creating" @click="createVersion">
                    <v-icon left small>mdi-plus</v-icon>{{ templates.length ? "Nova versão" : "Criar termo" }}
                </v-btn>
            </template>
            <v-data-table :headers="headers" :items="templates" hide-default-footer disable-pagination disable-sort
                no-data-text="Nenhuma versão do termo criada" class="pa-clickable-rows" @click:row="openVersion">
                <template v-slot:[`item.version`]="{ item }"><span class="font-weight-medium">v{{ item.version }}</span></template>
                <template v-slot:[`item.title`]="{ item }">
                    {{ item.title }}
                    <pa-badge v-if="item.id === currentTemplateId" variant="success" text="Vigente" class="ml-2" />
                </template>
                <template v-slot:[`item.status`]="{ item }"><pa-badge :status="item.status" /></template>
                <template v-slot:[`item.author_type`]="{ item }">{{ authorLabels[item.author_type] || item.author_type }}</template>
                <template v-slot:[`item.updated_at`]="{ item }"><span class="pa-muted">{{ formatDateTime(item.updated_at) }}</span></template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-btn small outlined @click.stop="openVersion(item)">{{ isEditable(item) ? "Editar" : "Abrir" }}</v-btn>
                </template>
            </v-data-table>
        </pa-section>

        <v-dialog :value="editor.open" fullscreen hide-overlay persistent no-click-animation transition="dialog-bottom-transition" content-class="pa-ui-overlay">
            <div v-if="editor.template" class="pa-editor-screen">
                <header class="pa-editor-screen__bar">
                    <div class="d-flex align-center" style="gap: 12px; min-width: 0;">
                        <v-btn icon @click="closeEditor" title="Fechar"><v-icon>mdi-close</v-icon></v-btn>
                        <div style="min-width: 0;">
                            <div class="pa-small pa-muted">Termo de adesão · versão {{ editor.template.version }}</div>
                            <div class="d-flex align-center" style="gap: 8px;">
                                <span class="pa-section-title text-truncate">{{ editor.template.title }}</span>
                                <pa-badge :status="editor.template.status" />
                                <span v-if="editor.dirty" class="pa-small pa-muted">· alterações não salvas</span>
                            </div>
                        </div>
                    </div>
                    <div class="d-flex align-center flex-wrap justify-end" style="gap: 8px;">
                        <v-btn v-for="action in actionsFor(editor.template)" :key="action.key"
                            :color="action.tone === 'primary' ? 'primary' : undefined" :depressed="action.tone === 'primary'"
                            :outlined="action.tone !== 'primary'" :class="{ 'pa-btn-danger': action.tone === 'danger' }"
                            :loading="editor.loading === action.key" :disabled="!!editor.loading && editor.loading !== action.key"
                            @click="$refs.form.emitAction(action)">
                            <v-icon v-if="action.icon" left small>{{ action.icon }}</v-icon>{{ action.label }}
                        </v-btn>
                    </div>
                </header>
                <main class="pa-editor-screen__content">
                    <v-alert v-if="previewErrors.length" type="warning" outlined dense class="mb-4">
                        <div class="font-weight-medium mb-1">Pendências para publicar</div>
                        <ul class="mb-0"><li v-for="message in previewErrors" :key="message">{{ message }}</li></ul>
                    </v-alert>
                    <plan-adhesion-template-form
                        ref="form"
                        :template="editor.template"
                        :catalog="catalog"
                        :class-names="classNames"
                        :voting-class-types="votingClassTypes"
                        :editable="isEditable(editor.template)"
                        @dirty="editor.dirty = $event"
                        @action="handleAction" />
                </main>
            </div>
        </v-dialog>

        <v-dialog v-model="reject.open" max-width="520" content-class="pa-ui-overlay">
            <v-card class="pa-card">
                <div class="pa-card__header">
                    <div>
                        <h3 class="pa-section-title">Rejeitar versão do termo</h3>
                        <p class="pa-section-description">O motivo é enviado à recuperanda, que pode corrigir e reenviar.</p>
                    </div>
                </div>
                <div class="pa-card__body">
                    <v-textarea v-model="reject.reason" label="Motivo" outlined rows="3" auto-grow maxlength="1000" counter hide-details="auto"></v-textarea>
                </div>
                <div class="pa-card__footer">
                    <v-btn outlined @click="reject.open = false">Cancelar</v-btn>
                    <v-btn color="error" depressed :disabled="!reject.reason.trim()" :loading="editor.loading === 'reject'" @click="confirmReject">Rejeitar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import PlanAdhesionTemplateForm from "./PlanAdhesionTemplateForm.vue";
import { AUTHOR_LABELS, errorMessage, formatDateTime, openBase64Pdf } from "./plan-adhesion.util";
import PaBadge from "./PaBadge.vue";
import PaSection from "./PaSection.vue";

// Versoes do termo de adesao. `mode` define o que o usuario pode fazer:
// admin (AJ) edita, aprova/rejeita e publica; client (recuperanda) edita os
// proprios rascunhos e envia para aprovacao. As rotas da API vem em `urls`.
export default {
    name: "PlanAdhesionTemplates",

    components: { PlanAdhesionTemplateForm, PaBadge, PaSection },

    props: {
        mode: { type: String, required: true, validator: (value) => ["admin", "client"].includes(value) },
        templates: { type: Array, default: () => [] },
        currentTemplateId: { type: Number, default: null },
        catalog: { type: Array, default: () => [] },
        classNames: { type: Object, default: () => ({}) },
        votingClassTypes: { type: Array, default: () => [] },
        urls: { type: Object, required: true },
    },

    data: () => ({
        creating: false,
        editor: { open: false, template: null, loading: null, dirty: false },
        reject: { open: false, reason: "" },
        previewErrors: [],
        authorLabels: AUTHOR_LABELS,
        headers: [
            { text: "Versão", value: "version", width: 80 },
            { text: "Título", value: "title" },
            { text: "Status", value: "status" },
            { text: "Autor", value: "author_type" },
            { text: "Atualizado em", value: "updated_at" },
            { text: "", value: "actions", align: "end" },
        ],
    }),

    computed: {
        intro() {
            return this.mode === "admin"
                ? "A versão publicada é a que os credores assinam. Versões enviadas pela recuperanda precisam da sua aprovação."
                : "Crie ou ajuste o termo e envie para aprovação do Administrador Judicial. Só a versão publicada pelo AJ fica disponível aos credores.";
        },
    },

    methods: {
        formatDateTime,

        notify(message, type = "success") {
            this.$dialog.notify[type](message, { position: "top-right", timeout: type === "error" ? 5000 : 2500 });
        },

        isEditable(template) {
            if (!template || !["draft", "rejected"].includes(template.status)) return false;
            return this.mode === "admin" || template.author_type === "client_admin";
        },

        actionsFor(template) {
            const editable = this.isEditable(template);
            const actions = [];
            actions.push({ key: "preview", label: "Prévia em PDF", icon: "mdi-file-eye-outline" });
            if (editable) actions.push({ key: "save", label: "Salvar rascunho" });

            if (this.mode === "client") {
                if (editable) actions.push({ key: "submit", label: "Enviar para aprovação do AJ", tone: "primary" });
                return actions;
            }

            if (template.status === "pending_approval") {
                actions.push({ key: "reject", label: "Rejeitar", tone: "danger" });
                actions.push({ key: "approve", label: "Aprovar e publicar", tone: "primary" });
            } else if (template.status === "approved" || (editable && template.author_type === "admin")) {
                actions.push({ key: "publish", label: "Publicar para os credores", tone: "primary" });
            }
            return actions;
        },

        async createVersion() {
            this.creating = true;
            try {
                const result = await axios.post(this.urls.create, {});
                this.$emit("changed");
                this.openTemplate(result.data.content);
                this.notify(`Versão ${result.data.content.version} criada.`);
            } catch (error) {
                this.notify(errorMessage(error), "error");
            } finally {
                this.creating = false;
            }
        },

        async openVersion(item) {
            try {
                const result = await axios.get(this.urls.template(item.id));
                this.openTemplate(result.data.content);
            } catch (error) {
                this.notify(errorMessage(error), "error");
            }
        },

        openTemplate(template) {
            this.previewErrors = [];
            this.editor = { open: true, template, loading: null, dirty: false };
        },

        async closeEditor() {
            if (this.editor.dirty && this.isEditable(this.editor.template)) {
                const discard = await this.$dialog.confirm({
                    title: "Alterações não salvas",
                    text: "Fechar o editor descarta as alterações que não foram salvas. Deseja fechar mesmo assim?",
                    actions: { false: "Continuar editando", true: { text: "Descartar e fechar", color: "error" } },
                });
                if (!discard) return;
            }
            this.editor.open = false;
            this.$emit("changed");
        },

        async save(payload) {
            const result = await axios.put(this.urls.template(this.editor.template.id), payload);
            this.editor.template = result.data.content;
            return result.data.content;
        },

        async handleAction(key, payload) {
            if (key === "reject") {
                this.reject = { open: true, reason: "" };
                return;
            }

            this.editor.loading = key;
            const id = this.editor.template.id;
            try {
                if (this.isEditable(this.editor.template)) await this.save(payload);

                if (key === "save") this.notify("Rascunho salvo.");
                if (key === "preview") {
                    const result = await axios.post(this.urls.action(id, "preview"), {});
                    this.previewErrors = result.data.content.errors || [];
                    openBase64Pdf(result.data.content.base64);
                }
                if (key === "submit") {
                    await axios.post(this.urls.action(id, "submit"));
                    this.notify("Termo enviado para aprovação do AJ.");
                }
                if (key === "approve") {
                    await axios.post(this.urls.action(id, "approve"), { publish: true });
                    this.notify("Termo aprovado e publicado.");
                }
                if (key === "publish") {
                    await axios.post(this.urls.action(id, "publish"));
                    this.notify("Termo publicado. Os credores passam a assinar esta versão.");
                }

                if (["submit", "approve", "publish"].includes(key)) {
                    const refreshed = await axios.get(this.urls.template(id));
                    this.editor.template = refreshed.data.content;
                    this.previewErrors = [];
                    this.$emit("changed");
                }
            } catch (error) {
                this.notify(errorMessage(error), "error");
            } finally {
                this.editor.loading = null;
            }
        },

        async confirmReject() {
            this.editor.loading = "reject";
            const id = this.editor.template.id;
            try {
                await axios.post(this.urls.action(id, "reject"), { reason: this.reject.reason.trim() });
                const refreshed = await axios.get(this.urls.template(id));
                this.editor.template = refreshed.data.content;
                this.reject.open = false;
                this.notify("Versão rejeitada. A recuperanda verá o motivo.");
                this.$emit("changed");
            } catch (error) {
                this.notify(errorMessage(error), "error");
            } finally {
                this.editor.loading = null;
            }
        },
    },
};
</script>

<style>
.pa-editor-screen {
    min-height: 100vh;
    background: #f5f5f7;
}

.pa-editor-screen__bar {
    position: sticky;
    top: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 24px;
    background: #fff;
    border-bottom: 1px solid #e4e4e7;
}

.pa-editor-screen__content {
    max-width: 1040px;
    margin: 0 auto;
    padding: 24px 24px 64px;
}
</style>
